import { useEffect, useState, type ReactElement } from 'react';
import { CORE_CONTRACT_VERSION, listActualWorkEntries, minutes, percent, taskProgressMode, type ActualWorkEntryInput, type Assignment } from '@bigint-project/core';

import { BaselineManagerDialog } from './components/baseline';
import { buildSampleProject } from './integration/sample-project';
import { ProjectInfoDialog } from './components/project-info/ProjectInfoDialog';
import { TaskDetailsDialog } from './components/task-details/TaskDetailsDialog';
import { TaskProgressDialog } from './components/task-progress';
import { TaskCustomFieldsDialog } from './components/custom-fields';
import { FileMenu } from './components/toolbar';
import { XlsxImportDialog } from './components/xlsx-import';
import { ProjectTemplateCreateDialog, ProjectTemplateExportDialog } from './components/project-template';
import { progressAsOf, selectExpectedProgressByTask } from './integration/derivations';
import { buildTaskImportPlan, buildXlsxProjectReplacePlan } from './integration/xlsx-workbook';
import type { ProjectTemplate } from './persistence/project-template';
import { noopAppPlatform } from './platform/noop';
import type { AppPlatform, TaskCollaborationPort } from './platform/ports';
import { useProjectStore } from './store/project-store';
import type { DependencyLineMode, TimeScale, ViewName } from './store/types';
import { ThemeToggle } from './theme';
import { BoardView } from './views/BoardView';
import { CalendarView } from './views/CalendarView';
import { CostTableView } from './views/CostTableView';
import { EvmReportView } from './views/EvmReportView';
import { GanttSplitView } from './views/GanttSplitView';
import { NetworkDiagramView } from './views/NetworkDiagramView';
import { LevelingPanel } from './views/LevelingPanel';
import { ProjectOverviewView } from './views/ProjectOverviewView';
import { ResourceSheetView } from './views/ResourceSheetView';
import { ResourceUsageView } from './views/ResourceUsageView';
import { ReportCenterView } from './views/ReportCenterView';
import { TimesheetView } from './views/TimesheetView';
import { VIEW_ORDER, VIEW_REGISTRY } from './views/view-registry';

/** 視圖名稱 → 實際元件。新增視圖只要在這裡與 `view-registry.ts` 各加一筆。 */
function renderView(
  view: ViewName,
  readOnly: boolean,
  collaboration: TaskCollaborationPort | undefined,
): ReactElement {
  switch (view) {
    case 'gantt':
      return <GanttSplitView readOnly={readOnly} />;
    case 'networkDiagram':
      return <NetworkDiagramView />;
    case 'resourceSheet':
      return <ResourceSheetView readOnly={readOnly} />;
    case 'resourceUsage':
      return <ResourceUsageView />;
    case 'costTable':
      return <CostTableView />;
    case 'projectOverview':
      return <ProjectOverviewView />;
    case 'evmReport':
      return <EvmReportView readOnly={readOnly} />;
    case 'reports':
      return <ReportCenterView readOnly={readOnly} />;
    case 'calendar':
      return <CalendarView readOnly={readOnly} />;
    case 'board':
      return <BoardView readOnly={readOnly} collaboration={collaboration} />;
    case 'timesheet':
      return <TimesheetView readOnly={readOnly} />;
    default:
      return <GanttSplitView readOnly={readOnly} />;
  }
}

const TIME_SCALES: readonly TimeScale[] = ['day', 'week', 'month', 'quarter'];
const TIME_SCALE_LABELS: Record<TimeScale, string> = {
  day: '日',
  week: '週',
  month: '月',
  quarter: '季',
};

const DEPENDENCY_LINE_MODES: readonly DependencyLineMode[] = ['all', 'selected', 'none'];
const DEPENDENCY_LINE_MODE_LABELS: Record<DependencyLineMode, string> = {
  all: '連接線：全部',
  selected: '連接線：僅選取',
  none: '連接線：隱藏',
};

function formatMoney(value: number, currency: string): string {
  return `${currency} ${value.toLocaleString('zh-TW', { maximumFractionDigits: 0 })}`;
}

type ProjectAppPlatform = AppPlatform<
  ProjectTemplate,
  Parameters<typeof ProjectTemplateExportDialog>[0]['onExport'] extends (input: infer T) => void ? T : never,
  Parameters<typeof ProjectTemplateCreateDialog>[0]['onCreate'] extends (input: infer T) => void ? T : never
>;

export interface AppProps {
  readonly platform?: ProjectAppPlatform;
  readonly readOnly?: boolean;
  readonly allowProjectReplacement?: boolean;
  readonly allowProjectRename?: boolean;
  /** 宿主自己顯示專案名稱與儲存狀態時（雲端工作區）關掉，避免重複標題與「未儲存」這類本機檔案語意。 */
  readonly showDocumentHeader?: boolean;
}

function useKeyboardShortcuts(
  platform: ProjectAppPlatform,
  readOnly: boolean,
  allowProjectReplacement: boolean,
): void {
  useEffect(() => {
    const handler = (event: KeyboardEvent): void => {
      const meta = event.metaKey || event.ctrlKey;
      if (!meta) return;
      const key = event.key.toLowerCase();
      if (
        (readOnly && (key === 'z' || key === 's' || key === 'o' || key === 'n')) ||
        (!allowProjectReplacement && (key === 'o' || key === 'n'))
      ) {
        event.preventDefault();
        return;
      }
      if (key === 'z' && event.shiftKey) {
        event.preventDefault();
        useProjectStore.getState().redo();
      } else if (key === 'z') {
        event.preventDefault();
        useProjectStore.getState().undo();
      } else if (key === 's' && event.shiftKey) {
        event.preventDefault();
        void platform.saveAs();
      } else if (key === 's') {
        event.preventDefault();
        void platform.save();
      } else if (key === 'o') {
        event.preventDefault();
        void platform.open();
      } else if (key === 'n') {
        event.preventDefault();
        void platform.new();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [allowProjectReplacement, platform, readOnly]);
}

export function App({
  platform = noopAppPlatform as ProjectAppPlatform,
  readOnly = false,
  allowProjectReplacement = true,
  allowProjectRename = true,
  showDocumentHeader = true,
}: AppProps = {}): ReactElement {
  const project = useProjectStore((state) => state.project);
  const projectName = useProjectStore((state) => state.project.name);
  const finishDate = useProjectStore((state) => state.project.finishDate);
  const currency = useProjectStore((state) => state.project.currency);
  const tasks = useProjectStore((state) => state.project.tasks);
  const selectedTaskIds = useProjectStore((state) => state.selectedTaskIds);
  const activeView = useProjectStore((state) => state.activeView);
  const timeScale = useProjectStore((state) => state.timeScale);
  const dependencyLineMode = useProjectStore((state) => state.dependencyLineMode);
  const scheduleMode = useProjectStore((state) => state.schedule.mode);
  const isDirty = useProjectStore((state) => state.file.isDirty);
  const filePath = useProjectStore((state) => state.file.filePath);
  const diagnostics = useProjectStore((state) => state.schedule.diagnostics);
  const setActiveView = useProjectStore((state) => state.setActiveView);
  const setTimeScale = useProjectStore((state) => state.setTimeScale);
  const setDependencyLineMode = useProjectStore((state) => state.setDependencyLineMode);
  const setScheduleMode = useProjectStore((state) => state.setScheduleMode);
  const runScheduleNow = useProjectStore((state) => state.runScheduleNow);
  const undo = useProjectStore((state) => state.undo);
  const redo = useProjectStore((state) => state.redo);
  const setBaseline = useProjectStore((state) => state.setBaseline);
  const clearBaseline = useProjectStore((state) => state.clearBaseline);
  const levelResources = useProjectStore((state) => state.levelResources);
  const updateProjectMeta = useProjectStore((state) => state.updateProjectMeta);
  const isProjectInfoOpen = useProjectStore((state) => state.isProjectInfoOpen);
  const openProjectInfo = useProjectStore((state) => state.openProjectInfo);
  const closeProjectInfo = useProjectStore((state) => state.closeProjectInfo);
  const isBaselineManagerOpen = useProjectStore((state) => state.isBaselineManagerOpen);
  const openBaselineManager = useProjectStore((state) => state.openBaselineManager);
  const closeBaselineManager = useProjectStore((state) => state.closeBaselineManager);
  const isTaskDetailsOpen = useProjectStore((state) => state.isTaskDetailsOpen);
  const openTaskDetails = useProjectStore((state) => state.openTaskDetails);
  const closeTaskDetails = useProjectStore((state) => state.closeTaskDetails);
  const updateTask = useProjectStore((state) => state.updateTask);
  const isTaskProgressOpen = useProjectStore((state) => state.isTaskProgressOpen);
  const openTaskProgress = useProjectStore((state) => state.openTaskProgress);
  const closeTaskProgress = useProjectStore((state) => state.closeTaskProgress);
  const isCustomFieldsOpen = useProjectStore((state) => state.isCustomFieldsOpen);
  const openCustomFields = useProjectStore((state) => state.openCustomFields);
  const closeCustomFields = useProjectStore((state) => state.closeCustomFields);
  const createTaskCustomField = useProjectStore((state) => state.createTaskCustomField);
  const renameTaskCustomField = useProjectStore((state) => state.renameTaskCustomField);
  const deleteTaskCustomField = useProjectStore((state) => state.deleteTaskCustomField);
  const isLeveling = useProjectStore((state) => state.leveling.isRunning);
  const showBaseline = useProjectStore((state) => state.showBaseline);
  const showCriticalPath = useProjectStore((state) => state.showCriticalPath);
  const setShowBaseline = useProjectStore((state) => state.setShowBaseline);
  const setShowCriticalPath = useProjectStore((state) => state.setShowCriticalPath);
  const pendingXlsxImport = useProjectStore((state) => state.pendingXlsxImport);
  const closeXlsxImport = useProjectStore((state) => state.closeXlsxImport);
  const importXlsxTasks = useProjectStore((state) => state.importXlsxTasks);
  const replaceProjectFromXlsx = useProjectStore((state) => state.replaceProjectFromXlsx);
  const isTemplateExportOpen = useProjectStore((state) => state.isTemplateExportOpen);
  const closeTemplateExport = useProjectStore((state) => state.closeTemplateExport);
  const pendingTemplate = useProjectStore((state) => state.pendingTemplate);
  const closeTemplateCreate = useProjectStore((state) => state.closeTemplateCreate);
  const recordActualWork = useProjectStore((state) => state.recordActualWork);
  const setTaskProgress = useProjectStore((state) => state.setTaskProgress);
  const setTaskProgressMode = useProjectStore((state) => state.setTaskProgressMode);
  const [progressError, setProgressError] = useState<string | null>(null);

  useEffect(() => {
    const previous = useProjectStore.getState().hostPolicy.readOnly;
    useProjectStore.getState().setHostReadOnly(readOnly);
    return () => useProjectStore.getState().setHostReadOnly(previous);
  }, [readOnly]);

  useKeyboardShortcuts(platform, readOnly, allowProjectReplacement);

  useEffect(() => {
    if (readOnly) return undefined;
    return platform.installLifecycle();
  }, [platform, readOnly]);

  const totalCost = tasks.reduce((sum, task) => sum + (task.fixedCost as number), 0);
  const criticalCount = tasks.filter((task) => task.isCritical).length;
  const selectedTask = selectedTaskIds.length === 1
    ? tasks.find((task) => task.id === selectedTaskIds[0]) ?? null
    : null;
  const selectedTaskAssignments = selectedTask === null
    ? []
    : project.assignments.filter((assignment) => assignment.taskId === selectedTask.id);
  const canUpdateProgress = selectedTask !== null && !selectedTask.isSummary && selectedTaskAssignments.length > 0;
  const resourceNameById = new Map(project.resources.map((resource) => [resource.id as string, resource.name]));
  const assignmentResourceById = new Map(project.assignments.map((assignment) => [assignment.id as string, assignment.resourceId as string]));
  const selectedTaskDailyEntries = selectedTask === null
    ? []
    : listActualWorkEntries(project, { taskId: selectedTask.id }).flatMap((entry) =>
      entry.assignmentId === null ? [] : [{
        entryId: entry.entryId,
        date: entry.date as string,
        assignmentId: entry.assignmentId,
        resourceName: resourceNameById.get(assignmentResourceById.get(entry.assignmentId as string) ?? '') ?? '',
        hours: (entry.actualWork as number) / 60,
        notes: entry.notes,
      }]);

  return (
    <div className="flex min-h-0 flex-1 flex-col bg-white text-slate-900 dark:bg-slate-900 dark:text-slate-100">
      <header className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-200 px-4 py-2 dark:border-slate-700">
        {showDocumentHeader ? (
          <div>
            <h1 className="text-sm font-semibold">
              {projectName}
              {isDirty ? ' •' : ''}
              {readOnly ? <span data-testid="host-read-only-indicator" className="ml-2 rounded bg-slate-200 px-1.5 py-0.5 text-[10px] font-medium text-slate-700 dark:bg-slate-700 dark:text-slate-200">唯讀</span> : null}
            </h1>
            <p className="text-xs text-slate-500 dark:text-slate-400">
              core v{CORE_CONTRACT_VERSION} · {filePath ?? '未儲存'}
            </p>
          </div>
        ) : null}
        <div className="ml-auto flex flex-wrap items-center justify-end gap-2 text-xs">
          <button
            type="button"
            data-testid="toolbar-project-info"
            disabled={readOnly}
            onClick={openProjectInfo}
            className="rounded border border-slate-300 px-2 py-1 hover:bg-slate-100 dark:border-slate-600 dark:hover:bg-slate-800"
          >
            專案資訊
          </button>
          <button
            type="button"
            data-testid="toolbar-task-details"
            disabled={selectedTask === null}
            onClick={openTaskDetails}
            className="rounded border border-slate-300 px-2 py-1 hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-50 dark:border-slate-600 dark:hover:bg-slate-800"
          >
            任務資訊
          </button>
          <button
            type="button"
            data-testid="toolbar-task-progress"
            disabled={readOnly || !canUpdateProgress}
            onClick={openTaskProgress}
            className="rounded border border-slate-300 px-2 py-1 hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-50 dark:border-slate-600 dark:hover:bg-slate-800"
          >
            更新進度
          </button>
          <button
            type="button"
            data-testid="toolbar-custom-fields"
            disabled={readOnly}
            onClick={openCustomFields}
            className="rounded border border-slate-300 px-2 py-1 hover:bg-slate-100 dark:border-slate-600 dark:hover:bg-slate-800"
          >
            自訂欄位
          </button>
          <button
            type="button"
            data-testid="toolbar-new-project"
            disabled={readOnly || !allowProjectReplacement}
            onClick={() => {
              if (!readOnly && allowProjectReplacement) void platform.new();
            }}
            className="rounded border border-slate-300 px-2 py-1 hover:bg-slate-100 dark:border-slate-600 dark:hover:bg-slate-800"
          >
            新增
          </button>
          <button
            type="button"
            data-testid="toolbar-open-project"
            disabled={readOnly || !allowProjectReplacement}
            onClick={() => {
              if (!readOnly && allowProjectReplacement) void platform.open();
            }}
            className="rounded border border-slate-300 px-2 py-1 hover:bg-slate-100 dark:border-slate-600 dark:hover:bg-slate-800"
          >
            開啟
          </button>
          <button
            type="button"
            data-testid="toolbar-save-project"
            disabled={readOnly}
            onClick={() => void platform.save()}
            className="rounded border border-slate-300 px-2 py-1 hover:bg-slate-100 dark:border-slate-600 dark:hover:bg-slate-800"
          >
            儲存
          </button>
          {/* 匯出範本不改動專案，唯讀也可用；web 沒有原生選單，這是唯一入口。 */}
          <button
            type="button"
            data-testid="toolbar-export-template"
            onClick={() => useProjectStore.getState().openTemplateExport()}
            className="rounded border border-slate-300 px-2 py-1 hover:bg-slate-100 dark:border-slate-600 dark:hover:bg-slate-800"
          >
            匯出範本
          </button>
          <FileMenu platform={platform} readOnly={readOnly} />
          <span className="mx-1 h-4 w-px bg-slate-300 dark:bg-slate-600" />
          <button
            type="button"
            data-testid="toolbar-undo"
            disabled={readOnly}
            onClick={() => undo()}
            className="rounded border border-slate-300 px-2 py-1 hover:bg-slate-100 dark:border-slate-600 dark:hover:bg-slate-800"
          >
            復原
          </button>
          <button
            type="button"
            data-testid="toolbar-redo"
            disabled={readOnly}
            onClick={() => redo()}
            className="rounded border border-slate-300 px-2 py-1 hover:bg-slate-100 dark:border-slate-600 dark:hover:bg-slate-800"
          >
            取消復原
          </button>
          <span className="mx-1 h-4 w-px bg-slate-300 dark:bg-slate-600" />
          <button
            type="button"
            data-testid="toolbar-load-sample"
            disabled={readOnly || !allowProjectReplacement}
            onClick={() => {
              if (!readOnly && allowProjectReplacement) {
                useProjectStore.getState().loadProject(buildSampleProject(), null);
              }
            }}
            className="rounded border border-slate-300 px-2 py-1 hover:bg-slate-100 dark:border-slate-600 dark:hover:bg-slate-800"
          >
            載入範例
          </button>
          <button
            type="button"
            data-testid="toolbar-set-baseline"
            disabled={readOnly}
            onClick={openBaselineManager}
            className="rounded border border-slate-300 px-2 py-1 hover:bg-slate-100 dark:border-slate-600 dark:hover:bg-slate-800"
          >
            設定基準線
          </button>
          <button
            type="button"
            data-testid="toolbar-toggle-baseline"
            aria-pressed={showBaseline}
            onClick={() => setShowBaseline(!showBaseline)}
            className={`rounded border px-2 py-1 ${
              showBaseline
                ? 'border-blue-600 bg-blue-600 text-white'
                : 'border-slate-300 hover:bg-slate-100 dark:border-slate-600 dark:hover:bg-slate-800'
            }`}
          >
            顯示基準線
          </button>
          <button
            type="button"
            data-testid="toolbar-toggle-critical-path"
            aria-pressed={showCriticalPath}
            onClick={() => setShowCriticalPath(!showCriticalPath)}
            className={`rounded border px-2 py-1 ${
              showCriticalPath
                ? 'border-blue-600 bg-blue-600 text-white'
                : 'border-slate-300 hover:bg-slate-100 dark:border-slate-600 dark:hover:bg-slate-800'
            }`}
          >
            顯示要徑
          </button>
          <button
            type="button"
            data-testid="toolbar-level-resources"
            disabled={readOnly || isLeveling}
            onClick={() => void levelResources()}
            className="rounded border border-slate-300 px-2 py-1 hover:bg-slate-100 disabled:opacity-50 dark:border-slate-600 dark:hover:bg-slate-800"
          >
            {isLeveling ? '撫平中…' : '資源撫平'}
          </button>
          <span className="mx-1 h-4 w-px bg-slate-300 dark:bg-slate-600" />
          <label className="flex items-center gap-1">
            排程
            <select
              data-testid="toolbar-schedule-mode"
              disabled={readOnly}
              value={scheduleMode}
              onChange={(event) => setScheduleMode(event.target.value as 'auto' | 'manual')}
              className="rounded border border-slate-300 bg-transparent px-1 py-0.5 dark:border-slate-600"
            >
              <option value="auto">自動</option>
              <option value="manual">手動</option>
            </select>
          </label>
          {scheduleMode === 'manual' ? (
            <button
              type="button"
              data-testid="toolbar-run-schedule"
              disabled={readOnly}
              onClick={() => runScheduleNow()}
              className="rounded border border-slate-300 px-2 py-1 hover:bg-slate-100 dark:border-slate-600 dark:hover:bg-slate-800"
            >
              立即重算
            </button>
          ) : null}
          <span className="mx-1 h-4 w-px bg-slate-300 dark:bg-slate-600" />
          <ThemeToggle />
        </div>
      </header>

      <nav className="flex flex-wrap items-center gap-1 border-b border-slate-200 px-4 py-1 text-xs dark:border-slate-700">
        {VIEW_ORDER.map((view) => (
          <button
            key={view}
            type="button"
            onClick={() => setActiveView(view)}
            className={`rounded px-2 py-1 ${
              activeView === view
                ? 'bg-blue-600 text-white'
                : 'text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800'
            }`}
          >
            {VIEW_REGISTRY[view].label}
          </button>
        ))}
        {activeView === 'gantt' ? (
          <>
            <select
              data-testid="toolbar-dependency-line-mode"
              aria-label="依賴連接線顯示方式"
              value={dependencyLineMode}
              onChange={(event) => setDependencyLineMode(event.target.value as DependencyLineMode)}
              className="ml-auto rounded border border-slate-300 bg-transparent px-1 py-0.5 dark:border-slate-600"
            >
              {DEPENDENCY_LINE_MODES.map((mode) => (
                <option key={mode} value={mode}>
                  {DEPENDENCY_LINE_MODE_LABELS[mode]}
                </option>
              ))}
            </select>
            <select
              aria-label="時間刻度"
              value={timeScale}
              onChange={(event) => setTimeScale(event.target.value as TimeScale)}
              className="rounded border border-slate-300 bg-transparent px-1 py-0.5 dark:border-slate-600"
            >
              {TIME_SCALES.map((scale) => (
                <option key={scale} value={scale}>
                  {TIME_SCALE_LABELS[scale]}
                </option>
              ))}
            </select>
          </>
        ) : null}
      </nav>

      <main className="relative min-h-0 flex-1 overflow-hidden p-2">
        {renderView(activeView, readOnly, platform.collaboration)}
        {readOnly ? null : <LevelingPanel />}
      </main>

      <footer className="flex flex-wrap items-center gap-x-4 gap-y-0.5 border-t border-slate-200 px-4 py-1 text-xs text-slate-500 dark:border-slate-700 dark:text-slate-400">
        <span>完成日：{finishDate}</span>
        <span>總成本：{formatMoney(totalCost, currency)}</span>
        <span>要徑任務數：{criticalCount}</span>
        {diagnostics.length > 0 ? (
          <span className="ml-auto text-amber-600 dark:text-amber-400">
            {diagnostics[diagnostics.length - 1]?.message}
          </span>
        ) : null}
      </footer>
      <ProjectInfoDialog
        isOpen={!readOnly && isProjectInfoOpen}
        project={project}
        allowProjectRename={allowProjectRename}
        onCancel={closeProjectInfo}
        onSave={(patch) => {
          updateProjectMeta(patch);
          closeProjectInfo();
        }}
      />
      <BaselineManagerDialog
        isOpen={!readOnly && isBaselineManagerOpen}
        project={project}
        onCancel={closeBaselineManager}
        onSave={(options) => {
          setBaseline(options);
          closeBaselineManager();
        }}
        onClear={(number) => clearBaseline(number)}
      />
      {selectedTask === null ? null : (
        <TaskDetailsDialog
          isOpen={isTaskDetailsOpen}
          readOnly={readOnly}
          collaboration={platform.collaboration}
          task={selectedTask}
          calendars={project.calendars}
          onCancel={closeTaskDetails}
          onSave={(patch) => {
            updateTask(selectedTask.id, patch);
            closeTaskDetails();
          }}
        />
      )}
      {selectedTask === null ? null : (
        <TaskProgressDialog
          isOpen={!readOnly && isTaskProgressOpen}
          task={selectedTask}
          assignments={selectedTaskAssignments}
          resources={project.resources}
          statusDate={project.statusDate}
          expectedProgress={selectExpectedProgressByTask(project, progressAsOf(project)).get(selectedTask.id as string) ?? null}
          dailyEntries={selectedTaskDailyEntries}
          errorMessage={progressError}
          onCancel={() => {
            setProgressError(null);
            closeTaskProgress();
          }}
          onSave={(entries) => {
            // 對話框只在 work 模式呼叫 onSave；任務若還停在 manual，要先把模式
            // 寫回去，否則 recordActualWork 會按 manual 規則處理（不反推進度）。
            if (taskProgressMode(selectedTask) !== 'work') {
              const modeResult = setTaskProgressMode(selectedTask.id, 'work');
              if (!modeResult.ok) {
                setProgressError(modeResult.error.message);
                return;
              }
            }
            // 工時表是實際工時的唯一真相：一律走 recordActualWork，依資源分組送出
            // （core 的 input 以單一資源為單位）。
            const byResource = new Map<string, ActualWorkEntryInput[]>();
            for (const entry of entries) {
              const assignment = selectedTaskAssignments.find((candidate) => candidate.id === entry.assignmentId);
              if (assignment === undefined) continue;
              const key = assignment.resourceId as string;
              const list = byResource.get(key) ?? [];
              list.push({
                date: entry.date as ActualWorkEntryInput['date'],
                taskId: selectedTask.id,
                assignmentId: assignment.id,
                actualWork: minutes(Math.round(entry.hours * 60)),
                notes: entry.notes,
                ...(entry.remainingHours === null
                  ? {}
                  : { remainingWork: minutes(Math.round(entry.remainingHours * 60)) }),
              });
              byResource.set(key, list);
            }
            for (const [resourceId, list] of byResource) {
              const result = recordActualWork({ resourceId: resourceId as Assignment['resourceId'], entries: list });
              if (!result.ok) {
                setProgressError(result.error.message);
                return;
              }
            }
            setProgressError(null);
            closeTaskProgress();
          }}
          onSaveManual={(percentComplete, actualStart, actualFinish) => {
            const result = setTaskProgress({
              taskId: selectedTask.id,
              percentComplete: percent(percentComplete),
              actualStart,
              actualFinish,
              progressMode: 'manual',
            });
            if (result.ok) {
              setProgressError(null);
              closeTaskProgress();
            } else {
              setProgressError(result.error.message);
            }
          }}
        />
      )}
      <TaskCustomFieldsDialog
        isOpen={!readOnly && isCustomFieldsOpen}
        definitions={project.customFields}
        onCancel={closeCustomFields}
        onCreate={(input) => {
          createTaskCustomField({
            name: input.name,
            kind: input.kind,
            lookup: input.lookup ?? null,
            defaultValue: input.defaultValue ?? null,
          });
        }}
        onRename={(id, name) => renameTaskCustomField(id, name)}
        onDelete={(id) => deleteTaskCustomField(id)}
      />
      {readOnly || pendingXlsxImport === null ? null : (
        <XlsxImportDialog
          isOpen
          workbook={pendingXlsxImport}
          onCancel={closeXlsxImport}
          onApply={({ sheetName, mapping, mode }) => {
            const sheet = pendingXlsxImport.sheets.find((candidate) => candidate.name === sheetName);
            if (sheet === undefined) return;
            const plan = buildTaskImportPlan(sheet, mapping);
            if (plan.diagnostics.length > 0 || plan.rows.length === 0) return;
            importXlsxTasks(plan.rows, mode);
            closeXlsxImport();
          }}
          onReplaceProject={() => {
            // Revalidate at the application boundary: the dialog never owns a mutable import plan.
            const verified = buildXlsxProjectReplacePlan(pendingXlsxImport);
            if (verified.diagnostics.length > 0) return;
            replaceProjectFromXlsx(verified, { clearProjectSnapshots: true });
            closeXlsxImport();
          }}
        />
      )}
      <ProjectTemplateExportDialog
        isOpen={isTemplateExportOpen}
        defaultName={projectName}
        onCancel={closeTemplateExport}
        onExport={(input) => {
          // 先關對話框再開原生存檔視窗：兩個 modal 疊在一起時鍵盤焦點會互搶。
          closeTemplateExport();
          void platform.exportTemplate(input);
        }}
      />
      {readOnly || pendingTemplate === null ? null : (
        <ProjectTemplateCreateDialog
          isOpen
          template={pendingTemplate}
          onCancel={closeTemplateCreate}
          onCreate={(input) => void platform.createFromTemplate(pendingTemplate, input)}
        />
      )}
    </div>
  );
}
